import mediaApi from '../api/client';
const POLICY_PATH = '/admin/media/upload-policy';
const UPLOAD_PATH = '/admin/media/upload';

// Keep one request ID and prepared payload per selected file, including retries.
const actions = new WeakMap<File, Map<string, { id: string; blob?: Blob; promise?: Promise<string> }>>();

async function prepareImage(file: File): Promise<Blob> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 30_000_000) {
    throw new Error('Please select a JPEG, PNG or WebP image smaller than 30 MB');
  }
  const image = await createImageBitmap(file);
  try {
    for (const [edge, quality] of [[1600, .85], [1400, .82], [1200, .8], [1024, .75], [960, .7], [800, .65]]) {
      const scale = Math.min(1, edge / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Image preparation failed');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size > 0 && blob.size <= 350_000) return blob;
    }
    throw new Error('Please select a smaller image');
  } finally { image.close(); }
}

export function uploadPublicImage(file: File, folder: string, onProgress?: (percent: number) => void): Promise<string> {
  let folders = actions.get(file);
  if (!folders) { folders = new Map(); actions.set(file, folders); }
  let action = folders.get(folder);
  if (action?.promise) return action.promise;
  if (!action) { action = { id: crypto.randomUUID() }; folders.set(folder, action); }
  const current = action;
  current.promise = Promise.resolve().then(async () => {
    let policy;
    try { policy = (await mediaApi.get(POLICY_PATH)).data; }
    catch (error: any) {
      if (error.response?.status === 404) policy = { public_upload_enabled: false };
      else throw new Error('Media upload is temporarily unavailable');
    }
    const enabled = policy?.public_upload_enabled === true;
    if (enabled && (!Array.isArray(policy.public_folders) || !policy.public_folders.includes(folder) || policy.idempotency_enabled !== true)) {
      throw new Error('Media upload is temporarily unavailable');
    }
    if (!enabled && policy?.public_upload_enabled !== false) throw new Error('Media upload is temporarily unavailable');
    if (!current.blob) current.blob = await prepareImage(file);
    const body = new FormData();
    body.append('file', current.blob, 'image.jpg');
    let url;
    if (enabled) {
      body.append('folder', folder);
      try {
        const response = await mediaApi.post(UPLOAD_PATH, body, {
          headers: { 'Content-Type': 'multipart/form-data', 'X-Upload-Request-ID': current.id }, timeout: 65_000,
          onUploadProgress: event => { if (event.total) onProgress?.(Math.round(event.loaded / event.total * 100)); },
        });
        url = response.data?.secure_url;
      } catch { throw new Error('The upload could not be confirmed. Please retry the same image.'); }
    } else {
      body.append('upload_preset', 'readora_unsigned');
      body.append('folder', `readora/${folder}`);
      const response = await fetch('https://api.cloudinary.com/v1_1/dhchvqvfg/image/upload', { method: 'POST', body });
      if (!response.ok) throw new Error('Image upload failed');
      url = (await response.json()).secure_url;
    }
    if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('The upload could not be confirmed');
    onProgress?.(100);
    folders!.delete(folder);
    return url;
  }).finally(() => { current.promise = undefined; });
  return current.promise;
}
